import { driver, Driver, DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "../styles/tourPopover.css";
import DOMPurify from "dompurify";
import { WidgetTour, Widget, WidgetTourStep, WidgetTourStepType } from "../../types";
import Utils from "../../utilities/Utils";

const POPOVER_CLASS = "bizzey-tour-popover";

// Tours can span multiple pages of the host site. When the host uses
// pushState-based routing (like our own PWA), we navigate the same way the
// app itself does - history.pushState + a synthetic popstate event - so its
// router picks up the change without a full reload tearing down JS state.
// sessionStorage progress and a hard-reload fallback remain for host pages
// that aren't listening (plain multi-page sites), or a real reload the user
// triggers - capped so a page that never settles on the target path can't
// turn into an infinite reload loop.
const STORAGE_KEY = "bizzey_tour_progress";
const MAX_NAVIGATE_ATTEMPTS = 2;
const SETTLE_DELAY_MS = 300;

interface TourProgress {
  widgetId: string;
  tourId: string;
  stepIndex: number;
  navigateAttempts: number;
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.innerText = value;
  return div.innerHTML;
}

function buildDescription(step: WidgetTourStep): string {
  const parts: string[] = [];

  if (
    step.type === WidgetTourStepType.VIDEO_POINTER &&
    (step as any).media_url
  ) {
    parts.push(
      `<video src="${escapeHtml(
        (step as any).media_url,
      )}" controls style="max-width: 100%; border-radius: 4px;"></video>`,
    );
  }

  // Unlike media_url (a raw attribute value), description is rich HTML
  // authored via Froala by an authenticated workspace agent. It's rendered
  // via innerHTML on the host's live site, so it's sanitized (rather than
  // escaped) to strip scripts/handlers while preserving the formatting.
  if (step.description) parts.push(DOMPurify.sanitize(step.description));

  return parts.join("");
}

// CSS for the content moved into description's shadow root - see
// isolateContent below.
const DESCRIPTION_SHADOW_CSS = `
  :host {
    display: block;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 15px;
    line-height: 1.5;
    color: #1f2328;
  }

  ul {
    list-style: disc outside;
    margin: 8px 0;
    padding-left: 24px;
  }

  ol {
    list-style: decimal outside;
    margin: 8px 0;
    padding-left: 24px;
  }

  li {
    display: list-item;
    margin: 4px 0;
  }

  a {
    color: inherit;
  }

  img, video {
    max-width: 100%;
    border-radius: 4px;
  }
`;

// CSS for the content moved into title's shadow root - see isolateContent
// below.
const TITLE_SHADOW_CSS = `
  .driver-popover-title-avatar {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #63778b;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    overflow: hidden;
  }

  .driver-popover-title-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .driver-popover-title-text {
    font-size: 15px;
    color: #6b7280;
  }
`;

// driver.js appends the popover wrapper straight to document.body, so its
// content sits directly in the host page's own DOM tree with no isolation -
// a host site's CSS reset (Tailwind preflight, Bootstrap, normalize.css,
// ...) can reach in and override anything tourPopover.css doesn't pin down
// with enough specificity (list-style being the one that actually bit us:
// a host zeroing out `ul`/`li` silently stripped bullets from step
// descriptions). Title and description hold the only genuinely open-ended
// markup here - author-written rich text, and our own generated
// avatar/name row - so each gets moved into its own shadow root: shadow
// boundaries can't be crossed by any selector, so nothing the host page
// does can reach in, no specificity race required. The chrome around them
// (wrapper, footer, buttons) stays plain DOM on purpose, since driver.js
// manages next/prev/close clicks via a single document-level listener that
// does `event.target.closest(...)` - shadow DOM retargets `event.target` to
// the shadow host for listeners outside the tree, which would silently
// break those clicks if the buttons themselves were shadow content.
function isolateContent(host: HTMLElement, css: string) {
  if (host.shadowRoot) return;
  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = css;
  shadow.appendChild(style);
  while (host.firstChild) shadow.appendChild(host.firstChild);
}

// Renders the Intercom-style "sender" line (avatar + "Author from
// Workspace") that replaces driver.js's plain bold title, using the tour's
// author rather than guessing at whichever agent happens to be first on
// the widget. Falls back to the widget's own name when the tour has no
// resolved author (e.g. the serializer couldn't load one).
function buildHeader(widget: Widget, tour: WidgetTour): string {
  const author = tour.author;
  const name = author?.name || widget.name || "";
  const from = author?.name ? widget.name : null;

  const avatar = author?.avatar_url
    ? `<img src="${escapeHtml(author.avatar_url)}" />`
    : escapeHtml(Utils.getInitials(name));

  const label = from
    ? `${escapeHtml(author.name)} from ${escapeHtml(from)}`
    : escapeHtml(name);

  return [
    `<span class="driver-popover-title-avatar">${avatar}</span>`,
    `<span class="driver-popover-title-text">${label}</span>`,
  ].join("");
}

class TourRunner {
  static driverInstance: Driver | null = null;

  static start(widget: Widget, tour: WidgetTour) {
    this.goToStep(widget, tour, 0, 0);
  }

  static resumeIfPending(widget: Widget) {
    const progress = this.read();
    if (!progress || progress.widgetId !== widget.id) return;

    const tour = (widget.tours || []).find(
      (tour) => tour.id === progress.tourId,
    );
    if (!tour) {
      this.clear();
      return;
    }

    this.goToStep(widget, tour, progress.stepIndex, progress.navigateAttempts);
  }

  private static sortedSteps(tour: WidgetTour): WidgetTourStep[] {
    return [...(tour.steps || [])].sort(
      (a, b) => (a.position || 0) - (b.position || 0),
    );
  }

  private static isCurrentPath(path: string): boolean {
    const normalize = (value: string) => value.replace(/\/+$/, "") || "/";
    return normalize(path) === normalize(window.location.pathname);
  }

  // Moves towards the given step, navigating the host page first if needed.
  // navigateAttempts tracks how many times we've already tried to get onto
  // the step's page for this step, so a host that keeps redirecting
  // elsewhere (or isn't SPA-routed at all) gets driven from wherever it
  // lands instead of looping forever.
  private static goToStep(
    widget: Widget,
    tour: WidgetTour,
    stepIndex: number,
    navigateAttempts: number,
  ) {
    const steps = this.sortedSteps(tour);
    const step = steps[stepIndex];

    if (!step) {
      this.clear();
      return;
    }

    if (
      step.page_path &&
      !this.isCurrentPath(step.page_path) &&
      navigateAttempts < MAX_NAVIGATE_ATTEMPTS
    ) {
      const pathBeforeNavigate = window.location.pathname;
      this.persist({
        widgetId: widget.id!,
        tourId: tour.id!,
        stepIndex,
        navigateAttempts: navigateAttempts + 1,
      });
      this.navigateTo(step.page_path, navigateAttempts);

      // Give the host router a moment to render (and settle any redirect of
      // its own, e.g. a list page bouncing to its default filter) before
      // checking again. If the path actually moved, trust wherever the
      // router landed instead of insisting on an exact match - otherwise a
      // step whose page_path is itself a redirect source (like a list page
      // that immediately bounces to its default filter) would never be
      // considered "arrived" and would escalate to a hard reload forever.
      window.setTimeout(() => {
        const moved = window.location.pathname !== pathBeforeNavigate;
        this.goToStep(
          widget,
          tour,
          stepIndex,
          moved ? MAX_NAVIGATE_ATTEMPTS : navigateAttempts + 1,
        );
      }, SETTLE_DELAY_MS);
      return;
    }

    this.persist({
      widgetId: widget.id!,
      tourId: tour.id!,
      stepIndex,
      navigateAttempts: 0,
    });
    this.drive(widget, tour, steps, stepIndex);
  }

  private static navigateTo(path: string, previousAttempts: number) {
    if (previousAttempts === 0) {
      // First attempt: soft-navigate so a pushState-based host router (like
      // this app's own) handles it without tearing the page down.
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate", { state: {} }));
    } else {
      // Soft navigation didn't get us there - no router was listening, or
      // it took us somewhere else entirely. Fall back to a real navigation.
      window.location.href = path;
    }
  }

  private static drive(
    widget: Widget,
    tour: WidgetTour,
    steps: WidgetTourStep[],
    stepIndex: number,
  ) {
    this.driverInstance?.destroy();

    const actionColor = tour.color || widget.action_color || "#1a73e8";
    const popoverBackgroundColor = tour.background_color || "#fff";

    this.driverInstance = driver({
      allowClose: true,
      showProgress: true,
      advanceOnClick: true,
      showButtons: ["next", "close"],
      progressText: "{{current}} of {{total}}",
      doneBtnText: "Done",
      nextBtnText: "Next",
      popoverClass: POPOVER_CLASS,
      steps: steps.map(
        (step): DriveStep => ({
          element: step.element || undefined,
          popover: {
            // driver.js only shows the title element (which we repurpose as
            // the avatar/sender row in onPopoverRender) when it's non-empty.
            title: step.title || " ",
            description: buildDescription(step),
            side: step.side as any,
            align: step.align as any,
          },
        }),
      ),
      onPopoverRender: (popover) => {
        popover.title.innerHTML = buildHeader(widget, tour);
        isolateContent(popover.title, TITLE_SHADOW_CSS);
        isolateContent(popover.description, DESCRIPTION_SHADOW_CSS);

        // driver.js normally re-positions the popover when an <img> inside
        // it finishes loading (see its own onRender image-load handling) by
        // querying `wrapper.querySelectorAll("img")` - which can't see into
        // the shadow root we just moved the description's images into.
        // Replicate it here so a step whose rich text embeds an image still
        // repositions once it loads instead of leaving the popover
        // misaligned against content that grew after layout.
        popover.description.shadowRoot?.querySelectorAll("img").forEach((img) => {
          if (img.complete) return;
          const reposition = () => this.driverInstance?.refresh();
          img.addEventListener("load", reposition, { once: true });
          img.addEventListener("error", reposition, { once: true });
        });

        popover.wrapper.style.setProperty("--bizzey-tour-accent", actionColor);
        popover.wrapper.style.setProperty(
          "--bizzey-tour-popover-background",
          popoverBackgroundColor,
        );
        popover.wrapper.style.backgroundColor = popoverBackgroundColor;
        popover.nextButton.style.backgroundColor = actionColor;
      },
      onNextClick: (_element, _step, opts) => {
        const currentIndex = opts.state.activeIndex ?? stepIndex;
        this.advanceTo(widget, tour, steps, currentIndex, currentIndex + 1);
      },
      onPrevClick: (_element, _step, opts) => {
        const currentIndex = opts.state.activeIndex ?? stepIndex;
        this.advanceTo(widget, tour, steps, currentIndex, currentIndex - 1);
      },
      onDestroyed: () => {
        this.clear();
        this.driverInstance = null;
      },
    });

    this.driverInstance.drive(stepIndex);
  }

  private static advanceTo(
    widget: Widget,
    tour: WidgetTour,
    steps: WidgetTourStep[],
    currentIndex: number,
    index: number,
  ) {
    const nextStep = steps[index];

    if (!nextStep) {
      this.driverInstance?.destroy();
      return;
    }

    // A step whose page_path is itself a redirect source (e.g. a list page
    // that immediately bounces to its default filter) means the browser can
    // already be sitting on a different URL than the declared page_path even
    // though we're on the "right" page. Comparing against the current
    // step's declared page_path - rather than window.location.pathname -
    // avoids re-triggering that redirect on every step that shares the page.
    const currentStep = steps[currentIndex];
    const sameDeclaredPage =
      !!currentStep && nextStep.page_path === currentStep.page_path;

    if (
      nextStep.page_path &&
      !sameDeclaredPage &&
      !this.isCurrentPath(nextStep.page_path)
    ) {
      this.goToStep(widget, tour, index, 0);
      return;
    }

    this.persist({
      widgetId: widget.id!,
      tourId: tour.id!,
      stepIndex: index,
      navigateAttempts: 0,
    });
    this.driverInstance?.moveTo(index);
  }

  private static persist(progress: TourProgress) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }

  private static read(): TourProgress | null {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private static clear() {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export default TourRunner;
